'use client'

// FaqPage — the FAQ accordion section (#118 RSC/FCP harvest, island).
//
// This is an INTERACTIVE `'use client'` island (search useState + reads the
// region FAQ collection via useRegionFaqs() — server-props with an Overmind
// fallback, Phase A2.1 — for the FAQ JSON-LD), NOT a byte-parity server twin.
// It is hosted on the RSC server path (app/[[...slug]]/RenderSectionsServer.jsx
// ISLAND_SECTIONS) as a STATIC-import client island — the same containment class
// as Video/PaymentPage/SpecsPage: a static import creates NO next/dynamic
// code-split (and so no streaming-Suspense boundary), so it cannot reproduce the
// #130 footer-reorder / React #418/#422 mismatch. The explicit top-of-file
// 'use client' is load-bearing: a transitive-only client boundary does NOT
// survive a static import into the SERVER module graph (RenderSectionsServer is
// an RSC), so without this directive the RSC path could not host it (the exact
// bit that broke slice-4 islands). FaqPage is also still statically imported by
// the client-path components/RenderSections.js (#130 MUST_BE_STATIC) — the
// directive is a harmless no-op there. Wiring it as an island flips the served,
// indexable /faq route (route -> the single faqPage `page` doc) to the full
// server-render path (0-JS server chrome + this island).
import React, { useState } from 'react'
import { useRegionFaqs } from '../../RegionContent'
import FaqCollapsibleList from '../FaqCollapsibleList'
import styles from './styles.module.css'
import SearchInput from '../../SearchInput'
import { searchMinLength } from '../../../utils/search'

const getAnswerText = (answer) => {
  if (!answer) return ''
  return answer
    .flatMap(a => a.children)
    .filter(a => !!a)
    .map(c => c.text)
    .filter(t => !!t)
    .join(' ')
}

const getFaqsForJsonLd = (allFaqs, faqSections) => {
  if (!allFaqs || !faqSections) return []

  const categoryTitles = faqSections
    .flatMap(section => {
      const categories = section.faqCategories || (section.faqCategory ? [section.faqCategory] : [])
      return categories.map(c => c.title)
    })
    .filter(Boolean)

  const filteredFaqs = categoryTitles.length > 0
    ? allFaqs.filter(f => !f.isHidden && f.category && categoryTitles.includes(f.category.title))
    : allFaqs.filter(f => !f.isHidden)

  return filteredFaqs.map(faq => ({
    questionName: faq.question,
    acceptedAnswerText: getAnswerText(faq.answer)
  })).filter(f => f.questionName && f.acceptedAnswerText)
}

// App-Router-safe FAQ JSON-LD. next-seo's <FAQPageJsonLd> renders through
// next/head, whose useContext(HeadManagerContext) returns null under App Router
// SSR — it THROWS ("Cannot read properties of null (reading 'useContext')"),
// which bails the nearest next/dynamic Suspense boundary and surfaces as
// React #419 ("server could not finish this Suspense boundary") on every
// /<region>/shop load (the shop template carries a faqPage section). We emit
// the SAME schema.org @graph next-seo would build as a raw
// <script type="application/ld+json"> node — App-Router-safe, renders under both
// routers, and (unlike the crashing next-seo path) actually SSRs the FAQ
// structured data into the HTML. Mirrors the raw-JSON-LD pattern used for the
// Breadcrumb/Product schema in app/[[...slug]]/RegionPageClient.jsx.
function buildFaqJsonLd(items) {
  if (!items || !items.length) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(q => ({
      '@type': 'Question',
      name: q.questionName,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.acceptedAnswerText,
      },
    })),
  }
}

function FaqPage({ faqs, hideSearch }) {
  const [search, setSearch] = useState(null)
  const allFaqs = useRegionFaqs()
  const faqJsonLdItems = getFaqsForJsonLd(allFaqs, faqs)
  const faqJsonLd = buildFaqJsonLd(faqJsonLdItems)

  return (
    <div className={styles.wrapper}>
      {faqJsonLd && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
      )}
      {
        !hideSearch && (
          <SearchInput
            onInputChange={(value) => setSearch(value)}
            placeholder={'Search for frequently asked questions'}
          />
        )
      }
      <div className={styles.faqsWrapper}>
        { !search && faqs.map(faq => <FaqCollapsibleList {...faq} key={faq._key}/>) }
        { search && <FaqCollapsibleList
          title="Search results"
          search={search}
          placeholderIfEmpty={
            search && !(search.length >= searchMinLength) ?
              `Please type in ${searchMinLength - search.length} more character${(searchMinLength - search.length) > 1 ? 's' : ''} to begin your FAQs search.`
              : "We couldn't find any FAQs containing your search terms."
          }/>
        }
      </div>
    </div>
  )
}

export default FaqPage
